
// frontend/src/components/Sports/SmartSportsWidget.js
import React, { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useGeoLocation } from '../../hooks/useGeoLocation';

import { ScoreboardGrid } from './ScoreboardGrid';
import { NewsGrid } from './NewsGrid';
import { Link } from 'react-router-dom';
import { ChevronRight, MapPin, Globe } from 'lucide-react';

const REGION_CONFIG = {
    'US': { label: 'United States', leagues: ['nfl', 'nba', 'nhl', 'eng.1'] },
    'CA': { label: 'Canada', leagues: ['nhl', 'nba', 'nfl', 'eng.1'] },
    'GB': { label: 'United Kingdom', leagues: ['eng.1', 'uefa.champions', 'nfl', 'nba'] },
    'ES': { label: 'Spain', leagues: ['esp.1', 'uefa.champions', 'eng.1', 'nba'] },
    'DE': { label: 'Germany', leagues: ['ger.1', 'uefa.champions', 'eng.1', 'nba'] },
    'IT': { label: 'Italy', leagues: ['ita.1', 'uefa.champions', 'eng.1', 'nba'] },
    'EU': { label: 'Europe', leagues: ['uefa.champions', 'eng.1', 'nba', 'nfl'] },
    'AU': { label: 'Australia', leagues: ['eng.1', 'nba', 'nfl', 'nhl'] },
    'GLOBAL': { label: 'Global', leagues: ['eng.1', 'nba', 'nfl', 'nhl', 'uefa.champions'] }
};

export const SmartSportsWidget = () => {
    const { region } = useGeoLocation();
    const config = REGION_CONFIG[region] || REGION_CONFIG['GLOBAL'];
    const [activeLeague, setActiveLeague] = useState(config.leagues[0]);

    useEffect(() => {
        // Reset tab when region changes
        setActiveLeague(config.leagues[0]);
    }, [region]);

    return (
        <section className="py-20 px-4 relative overflow-hidden">
            <div className="max-w-7xl mx-auto">
                {/* Header */}
                <div className="flex flex-col md:flex-row items-center justify-between mb-10 gap-6">
                    <div>
                        <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-white/5 border border-white/10 mb-4 backdrop-blur-sm">
                            <MapPin className="w-4 h-4 text-primary" />
                            <span className="text-xs font-bold text-white uppercase tracking-wider">
                                Trending Now in {config.label}
                            </span>
                        </div>
                        <h2 className="text-4xl font-bold text-white mb-2">Live Sports Action</h2>
                        <p className="text-muted-foreground">Real-time scores from top leagues in your region.</p>
                    </div>

                    {/* League Toggles */}
                    <div className="flex flex-wrap gap-2 bg-white/5 p-1 rounded-xl border border-white/10 backdrop-blur-sm">
                        {config.leagues.map((league) => (
                            <button
                                key={league}
                                onClick={() => setActiveLeague(league)}
                                className={`px-4 py-2 rounded-lg text-sm font-bold transition-all ${activeLeague === league
                                    ? 'bg-primary text-black shadow-lg'
                                    : 'text-muted-foreground hover:text-white'
                                    }`}
                            >
                                {getLeagueName(league)}
                            </button>
                        ))}
                    </div>
                </div>

                {/* Scoreboard Content */}
                <div className="relative min-h-[300px]">
                    <AnimatePresence mode="wait">
                        <motion.div
                            key={activeLeague}
                            initial={{ opacity: 0, y: 10 }}
                            animate={{ opacity: 1, y: 0 }}
                            exit={{ opacity: 0, y: -10 }}
                            transition={{ duration: 0.3 }}
                        >
                            <ScoreboardGrid
                                sport={getSport(activeLeague)}
                                league={getLeagueId(activeLeague)}
                                limit={6}
                            />
                        </motion.div>
                    </AnimatePresence>
                </div>

                {/* News Injection */}
                <div className="mt-8 pt-8 border-t border-white/5">
                    <div className="flex items-center gap-2 mb-6">
                        <span className="w-1 h-6 bg-primary rounded-full"></span>
                        <h3 className="text-lg font-bold text-white">Latest {getLeagueName(activeLeague)} Headlines</h3>
                    </div>
                    <NewsGrid
                        category={getNewsCategory(activeLeague)}
                        initialCount={4}
                        showLoadMore={false}
                        showHeader={false}
                    />
                </div>

                {/* Footer CTA */}
                <div className="flex justify-center mt-10">
                    <Link
                        to="/sports"
                        className="group inline-flex items-center gap-2 text-white font-medium hover:text-primary transition-colors"
                    >
                        <span>View All Live Scores</span>
                        <ChevronRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                    </Link>
                </div>
            </div>
        </section >
    );
};

// Helpers
const getLeagueName = (slug) => {
    const map = {
        'nfl': 'NFL', 'nba': 'NBA', 'nhl': 'NHL', 'eng.1': 'Premier League',
        'esp.1': 'La Liga', 'ger.1': 'Bundesliga', 'ita.1': 'Serie A',
        'uefa.champions': 'Champions League', 'f1': 'F1'
    };
    return map[slug] || slug.toUpperCase();
};

const getSport = (slug) => {
    if (['nba'].includes(slug)) return 'basketball';
    if (['nfl'].includes(slug)) return 'football';
    if (['nhl'].includes(slug)) return 'hockey';
    if (['f1'].includes(slug)) return 'racing';
    return 'soccer';
};

const getNewsCategory = (slug) => {
    if (['nba'].includes(slug)) return 'nba';
    if (['nfl'].includes(slug)) return 'nfl';
    if (['nhl'].includes(slug)) return 'nhl';
    if (['f1'].includes(slug)) return 'f1';
    return 'soccer';
};

const getLeagueId = (slug) => {
    return slug; // The slug in config matches the ID needed for the API in most cases now
};
